import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowRightCircle,
  ChevronDown,
  ChevronUp,
  Loader2,
} from 'lucide-react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import {
  continueGoalsFromPreviousEpisode,
  getGoalsToContinue,
} from '../../services/api';
import type {
  CarryForwardGoal,
  CarryForwardGoalsResponse,
  ContinueGoalSelection,
} from '../../services/api/types.gen';
import { useToast } from '../../hooks/useToastContext';
import { groupByDiscipline } from '../../constants/disciplines';
import {
  formatDateForDisplay,
  formatDateToISO,
  parseISOToLocalDate,
} from '../../utils/dateUtils';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import {
  arePushGoalFieldsValid,
  GOAL_TYPE_OPTIONS,
  type PushGoalFieldValues,
} from '../../utils/goals';
import PushGoalFields from './PushGoalFields';

interface ContinueGoalsFromPreviousEpisodeProps {
  episodeId: string;
  /** Called after goals are continued, so the parent refreshes its goal lists. */
  onContinued?: () => void;
}

/**
 * Goals start checked. An imported goal the External EMR already pushed here
 * is disabled and stays unchecked; the server skips it if it arrives anyway.
 */
const initialSelection = (goals: CarryForwardGoal[]): Set<string> =>
  new Set(
    goals
      .filter(goal => !goal.already_on_episode)
      .map(goal => goal.patient_goal_id)
  );

/** What a goal lands as when the clinician does not edit it. */
const defaultFields = (
  goal: CarryForwardGoal,
  episodeEnd: Date | undefined
): PushGoalFieldValues => ({
  instruction: goal.instruction ?? '',
  length: goal.length,
  targetDate: episodeEnd,
  isForEndOfEpisode: true,
});

const isEdited = (
  goal: CarryForwardGoal,
  fields: PushGoalFieldValues
): boolean =>
  fields.instruction.trim() !== (goal.instruction ?? '').trim() ||
  fields.length !== goal.length ||
  !fields.isForEndOfEpisode;

const lengthLabel = (length: string): string =>
  GOAL_TYPE_OPTIONS.find(option => option.value === length)?.label ?? length;

/**
 * EMR-2754: a new episode's Goals tab offers the previous episode's open goals.
 * The clinician confirms which ones continue; nothing carries on its own. The
 * button shows only when the previous episode has a goal left to continue.
 * Render it only for a holder of `goals:carry-forward` (the server gates both
 * routes and re-checks at the patient's clinic).
 */
const ContinueGoalsFromPreviousEpisode: React.FC<
  ContinueGoalsFromPreviousEpisodeProps
> = ({ episodeId, onContinued }) => {
  const [offer, setOffer] = useState<CarryForwardGoalsResponse | null>(null);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Per-goal edits, keyed by goal id. A goal with no entry lands as-is.
  const [edits, setEdits] = useState<Record<string, PushGoalFieldValues>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const { success: showSuccess, error: showError } = useToast();

  const loadOffer =
    useCallback(async (): Promise<CarryForwardGoalsResponse | null> => {
      const response = await getGoalsToContinue({
        path: { episode_id: episodeId },
      });
      if (response.error || !response.data) {
        // The offer is optional: a failed read hides the button rather than
        // interrupting the Goals tab.
        console.error('Failed to load goals to continue', response.error);
        setOffer(null);
        return null;
      }
      setOffer(response.data);
      return response.data;
    }, [episodeId]);

  useEffect(() => {
    setOffer(null);
    void loadOffer();
  }, [loadOffer]);

  const goals = useMemo(() => offer?.goals ?? [], [offer]);
  const groups = useMemo(
    () => groupByDiscipline(goals, goal => goal.discipline),
    [goals]
  );

  // "Already on this episode" rows cannot be pushed. An offer made only of
  // them has nothing to save, so the button stays hidden.
  if (goals.every(goal => goal.already_on_episode)) return null;

  const episodeStart = offer?.target_episode_start_date
    ? parseISOToLocalDate(offer.target_episode_start_date)
    : undefined;
  const episodeEnd = offer?.target_episode_end_date
    ? parseISOToLocalDate(offer.target_episode_end_date)
    : undefined;

  const fieldsFor = (goal: CarryForwardGoal): PushGoalFieldValues =>
    edits[goal.patient_goal_id] ?? defaultFields(goal, episodeEnd);

  // Only checked goals the clinician changed carry edits to the server.
  const editedSelection = goals.filter(
    goal =>
      selected.has(goal.patient_goal_id) &&
      edits[goal.patient_goal_id] !== undefined &&
      isEdited(goal, edits[goal.patient_goal_id])
  );
  const hasInvalidEdit = editedSelection.some(
    goal => !arePushGoalFieldsValid(edits[goal.patient_goal_id])
  );

  const openDialog = () => {
    setSelected(initialSelection(goals));
    setEdits({});
    setExpanded(new Set());
    setOpen(true);
  };

  const toggleEditor = (goalId: string) => {
    setExpanded(previous => {
      const next = new Set(previous);
      if (next.has(goalId)) next.delete(goalId);
      else next.add(goalId);
      return next;
    });
  };

  const toggle = (goal: CarryForwardGoal) => {
    if (goal.already_on_episode) return;
    const goalId = goal.patient_goal_id;
    setSelected(previous => {
      const next = new Set(previous);
      if (next.has(goalId)) next.delete(goalId);
      else next.add(goalId);
      return next;
    });
  };

  const save = async () => {
    setIsSaving(true);
    try {
      const edited = new Set(editedSelection.map(goal => goal.patient_goal_id));
      const body: ContinueGoalSelection[] = [...selected].map(goalId => {
        const fields = edits[goalId];
        if (!edited.has(goalId) || !fields?.targetDate) {
          return { patient_goal_id: goalId };
        }
        return {
          patient_goal_id: goalId,
          instruction: fields.instruction.trim(),
          length: fields.length,
          target_date: formatDateToISO(fields.targetDate),
          is_for_end_of_episode: fields.isForEndOfEpisode,
        };
      });
      const response = await continueGoalsFromPreviousEpisode({
        path: { episode_id: episodeId },
        body: { goals: body },
        throwOnError: true,
      });
      // Count only what this save wrote; goals already continued by an
      // earlier save are left alone.
      const count = response.data.continued.length;
      showSuccess(
        count === 0
          ? 'These goals were already pushed'
          : count === 1
            ? '1 goal pushed'
            : `${count} goals pushed`
      );
      setOpen(false);
      onContinued?.();
      await loadOffer();
    } catch (error) {
      console.error('Failed to continue goals', error);
      showError(
        'Goals were not pushed',
        getBusinessErrorMessage(error) ?? 'Please try again.'
      );
      // A goal may have changed since the list loaded. Re-read it and keep
      // the dialog open with the clinician's edits. Uncheck any goal the
      // fresh list no longer offers, so the retry names only current goals.
      const fresh = await loadOffer();
      const pushable = new Set(
        (fresh?.goals ?? [])
          .filter(goal => !goal.already_on_episode)
          .map(goal => goal.patient_goal_id)
      );
      setSelected(
        previous => new Set([...previous].filter(id => pushable.has(id)))
      );
    } finally {
      setIsSaving(false);
    }
  };

  const previousPeriod =
    offer?.source_episode_start_date && offer.source_episode_end_date
      ? `${formatDateForDisplay(offer.source_episode_start_date)} – ${formatDateForDisplay(offer.source_episode_end_date)}`
      : null;

  return (
    <>
      <Button
        size='sm'
        variant='outline'
        onClick={openDialog}
        className='flex items-center gap-1'
      >
        <ArrowRightCircle className='h-4 w-4' />
        Push goals from previous episode
      </Button>
      <Dialog open={open} onOpenChange={next => !isSaving && setOpen(next)}>
        <DialogContent className='flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-2xl flex-col'>
          <DialogHeader>
            <DialogTitle>Push goals from previous episode</DialogTitle>
            <DialogDescription>
              {previousPeriod
                ? `These goals were still open when the episode of ${previousPeriod} ended.`
                : 'These goals were still open when the previous episode ended.'}{' '}
              Uncheck any goal that should stop. Pushed goals keep their
              instruction and term and target the end of this episode. Use Edit
              to change one goal.
            </DialogDescription>
          </DialogHeader>
          <div className='min-h-0 flex-1 space-y-4 overflow-y-auto pr-1'>
            {groups.map(group => (
              <section key={group.discipline} className='space-y-2'>
                <h4 className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                  {group.discipline}
                </h4>
                <ul className='space-y-2'>
                  {group.items.map(goal => {
                    const goalId = goal.patient_goal_id;
                    const id = `continue-goal-${goalId}`;
                    const isChecked = selected.has(goalId);
                    const isDisabled = goal.already_on_episode;
                    const isOpen = isChecked && expanded.has(goalId);
                    const fields = fieldsFor(goal);
                    const edited =
                      edits[goalId] !== undefined && isEdited(goal, fields);
                    const target =
                      fields.isForEndOfEpisode || !fields.targetDate
                        ? 'end of episode'
                        : formatDateForDisplay(
                            formatDateToISO(fields.targetDate)
                          );
                    return (
                      <li
                        key={goalId}
                        className='space-y-2 rounded-md border p-2'
                      >
                        <div className='flex items-start gap-2'>
                          <Checkbox
                            id={id}
                            checked={isChecked}
                            disabled={isDisabled}
                            onCheckedChange={() => toggle(goal)}
                            className='mt-0.5'
                          />
                          <label
                            htmlFor={id}
                            className={`min-w-0 flex-1 space-y-0.5 ${isDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                          >
                            <span className='block break-words text-sm'>
                              {goal.instruction || goal.name}
                            </span>
                            {isDisabled ? (
                              <span className='block text-xs text-muted-foreground'>
                                Already on this episode.
                              </span>
                            ) : (
                              isChecked && (
                                <span className='block text-xs text-muted-foreground'>
                                  {edited ? 'Edited · ' : ''}
                                  {lengthLabel(fields.length)} · Target:{' '}
                                  {target}
                                </span>
                              )
                            )}
                          </label>
                          {isChecked && (
                            <Button
                              size='sm'
                              variant='ghost'
                              onClick={() => toggleEditor(goalId)}
                              aria-expanded={isOpen}
                              aria-label={`${isOpen ? 'Close edit for' : 'Edit'} ${goal.instruction || goal.name}`}
                              className='flex shrink-0 items-center gap-1'
                            >
                              {isOpen ? 'Done' : 'Edit'}
                              {isOpen ? (
                                <ChevronUp className='h-4 w-4' />
                              ) : (
                                <ChevronDown className='h-4 w-4' />
                              )}
                            </Button>
                          )}
                        </div>
                        {isOpen && (
                          <div className='border-t pt-2 sm:pl-6'>
                            <PushGoalFields
                              idPrefix={id}
                              values={fields}
                              onChange={values =>
                                setEdits(previous => ({
                                  ...previous,
                                  [goalId]: values,
                                }))
                              }
                              minDate={episodeStart}
                              maxDate={episodeEnd}
                              endOfEpisodeLabel='For end of this episode'
                              showErrors
                            />
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
          <DialogFooter className='flex-col-reverse gap-2 sm:flex-row'>
            <Button
              variant='outline'
              onClick={() => setOpen(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void save()}
              disabled={isSaving || selected.size === 0 || hasInvalidEdit}
            >
              {isSaving && <Loader2 className='mr-1 h-4 w-4 animate-spin' />}
              {selected.size === 1
                ? 'Push 1 goal'
                : `Push ${selected.size} goals`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContinueGoalsFromPreviousEpisode;
